import { yupResolver } from '@hookform/resolvers/yup'
import { parseISO } from '@if/core/date'
import { EFinancingProgram, getECreditApplicationStepList } from '@if/core/types'
import { ControlledDateTimePicker } from '@if/mui'
import { Grid } from '@mui/material'
import { memo, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { InputTextField, PageError, SelectComponent } from '../../../components'
import ActionsDialog from '../../../components/ActionsDialog'
import { TRootState, useAppSelector } from '../../../data/store'
import { appSelectors } from '../../../data/store/AppStore'
import { CreditApplicationTask, creditTaskSchema, SelectValueListItem } from '../../../data/types'
import { TranslatedEnum } from '../../../data/types/TranslatedEnum'
import { reportErrorToConsole } from '../../../services/error-logger'

interface Props {
  open: boolean
  title: string
  defaultValues: Partial<CreditApplicationTask>
  onConfirm: (data: CreditApplicationTask) => void
  onCancel: () => void
  disableConfirm?: boolean
}

const EditTaskDialog = ({ defaultValues: pDefaultValues, onConfirm, onCancel, open, title, disableConfirm }: Props) => {
  const { t } = useTranslation()
  const error = useAppSelector(appSelectors.getBusinessError)
  const { financingProgramId } = useParams<{ financingProgramId: EFinancingProgram }>()
  const lang = useAppSelector(appSelectors.getCurrentLang) as keyof TranslatedEnum
  const allTaskTypes = useAppSelector((state: TRootState) => appSelectors.getAllTaskTypes(state, financingProgramId!))
  const taskTypesAddableManually = useAppSelector((state: TRootState) =>
    appSelectors.getTaskTypesAddableManually(state, financingProgramId!),
  )

  const [selectContent, setSelectContent] = useState<SelectValueListItem[]>([])

  const defaultValues = useMemo(
    () => ({
      ...pDefaultValues,
      reminderOn:
        pDefaultValues.reminderOn && typeof pDefaultValues.reminderOn === 'string'
          ? parseISO(pDefaultValues.reminderOn)
          : pDefaultValues.reminderOn,
    }),
    [pDefaultValues],
  )

  const {
    register,
    reset,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CreditApplicationTask>({
    mode: 'onBlur',
    defaultValues,
    resolver: yupResolver(creditTaskSchema),
  })

  const buildSelectContent = useMemo(() => {
    return Object.values(taskTypesAddableManually)
      .filter((item) => taskTypesAddableManually[item.id])
      .map((item) => ({
        label: allTaskTypes[item.id][lang],
        value: item.id,
      }))
  }, [taskTypesAddableManually, allTaskTypes, lang])

  useEffect(() => {
    if (defaultValues.id && Object.values(taskTypesAddableManually).every((x) => x.id !== defaultValues.typeId))
      setSelectContent([{ label: allTaskTypes[defaultValues.typeId!][lang] as string, value: defaultValues.typeId! }])
    else setSelectContent(buildSelectContent)

    reset(defaultValues)
  }, [defaultValues, allTaskTypes, buildSelectContent, lang, reset, taskTypesAddableManually])

  const onSubmit = (data: CreditApplicationTask) => {
    onConfirm(data)
  }
  const creditApplicationStepOptions = useMemo<SelectValueListItem[]>(
    () =>
      getECreditApplicationStepList().map((value) => ({
        label: t(`ECreditApplicationStep.${value}`, { ns: 'enum' }),
        value,
      })),
    [t],
  )

  return (
    <ActionsDialog
      title={title}
      onCancel={onCancel}
      onConfirm={handleSubmit(onSubmit, reportErrorToConsole)}
      open={open}
      disabled={disableConfirm}
    >
      <PageError errors={error} />
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <SelectComponent items={selectContent} label="Type" {...register('typeId')} error={errors?.typeId} />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <SelectComponent
            items={creditApplicationStepOptions}
            label={t('common.requiredFor')}
            {...register('requiredBeforeStep')}
            error={errors?.requiredBeforeStep}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <ControlledDateTimePicker
            name="reminderOn"
            control={control}
            label={t('tasks.reminder')}
            disablePast={defaultValues.id === null}
            fullWidth
            clearable
          />
        </Grid>
        <Grid size={12}>
          <InputTextField label="Description" {...register('description')} error={errors?.description} />
        </Grid>
      </Grid>
    </ActionsDialog>
  )
}
export default memo(EditTaskDialog)
